import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Button, Table, Modal, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faFileInvoice,
  faCreditCard,
  faDownload,
  faEye,
  faChartLine,
  faHandHoldingDollar,
  faArrowUp,
  faBolt,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faProjectDiagram,
  faArrowTrendUp,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import BrokerApiService from '../../services/brokerApiService';
import {
  BROKER_PRICING_TIERS,
  getCurrentTier,
  calculateMonthlyTotal,
  isApproachingTierLimit,
  getNextTier
} from '../../constants/brokeragePricing';
import ImplementationStatusCard from './ImplementationStatusCard';
import SimpleBrokeragePricingCard from './SimpleBrokeragePricingCard';
import BrokerageSubscriptionModal from './BrokerageSubscriptionModal';
import BrokerPricingSubscriptionModal from './BrokerPricingSubscriptionModal';
import axiosInstance from '../../helpers/api';
import { loadStripe } from '@stripe/stripe-js';
import configService from '../../services/configService';
import { toast } from 'react-hot-toast';

interface BrokerBillingProps {
  brokerageId?: string;
  data?: any;
}

interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  description: string;
  agentCount: number;
  pdfUrl?: string;
  hostedUrl?: string;
  lines?: any[];
  breakdown: {
    baseFee: number;
    agentFees?: number;
    professionalUpgrades: number;
    premiumUpgrades: number;
  };
}

interface BillingData {
  currentBilling: {
    monthlyTotal: number;
    annualProjection: number;
    baseFee: number;
    agentFees: number;
    upgradeRevenue: number;
    totalAgentCount: number;
    agentBreakdown: {
      essential: { count: number; fee: number; total: number };
      professional: { count: number; fee: number; total: number };
      premium: { count: number; fee: number; total: number };
      dealai: { count: number; fee: number; total: number };
    };
    nextBillingDate: string;
    paymentMethod: string;
  };
  revenueHistory: Array<{
    month: string;
    amount: number;
    growth: number;
  }>;
  savingsComparison: {
    individualCost: number;
    brokerageCost: number;
    totalSavings: number;
    savingsPercentage: number;
  };
}

const BrokerBilling: React.FC<BrokerBillingProps> = ({ 
  brokerageId, 
  data 
}) => {
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // KOPS-329: New implementation status state
  const [implementationPaid] = useState<boolean>(false);
  const [implementationDate] = useState<Date | null>(null);
  const [onboardingStatus] = useState<any>(null);
  const [pricingTier] = useState<'stability' | 'small' | 'medium' | 'large'>('small');
  const [hasAiWebsite] = useState<boolean>(false);

  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'stability' | 'small' | 'medium' | 'large'>('small');
  const [selectedPrice, setSelectedPrice] = useState<number>(0);

  // New broker pricing subscription modal state
  const [showBrokerPricingModal, setShowBrokerPricingModal] = useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<'per-office' | 'transaction-coordinator'>('per-office');
  const [processingSubscription, setProcessingSubscription] = useState(false);

  // Stripe instance
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);

  // Fetch billing data from API
  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!brokerageId) {
          setError('No brokerage ID provided');
          setLoading(false);
          return;
        }

        const response = await BrokerApiService.getBillingInfo(brokerageId);
        
        if (response.success) {
          const billing = response.billing;
          const agentCounts = billing.current_month.agent_counts;

          // Calculate agent fees safely
          const calculatedAgentFees = (billing.current_month as any).agent_fees ?? (agentCounts.total * 50);

          setBillingData({
            currentBilling: {
              monthlyTotal: billing.current_month.total_amount,
              annualProjection: billing.current_month.total_amount * 12,
              baseFee: billing.current_month.base_fee,
              agentFees: calculatedAgentFees,
              upgradeRevenue: 0, // AI upgrades paid by agents directly
              totalAgentCount: agentCounts.total,
              agentBreakdown: {
                essential: {
                  count: agentCounts.essential,
                  fee: 50, // Brokerage pays $50 per agent
                  total: agentCounts.essential * 50
                },
                professional: {
                  count: agentCounts.professional,
                  fee: 50, // Brokerage still pays $50, agent pays $200 for AI
                  total: agentCounts.professional * 50
                },
                premium: {
                  count: (agentCounts as any).agentk ?? agentCounts.premium,
                  fee: 50, // Brokerage still pays $50, agent pays $450 for AI
                  total: ((agentCounts as any).agentk ?? agentCounts.premium) * 50
                },
                dealai: {
                  count: (agentCounts as any).dealai ?? 0,
                  fee: 50, // Brokerage still pays $50, agent pays $300 for AI
                  total: ((agentCounts as any).dealai ?? 0) * 50
                }
              },
              nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
              paymentMethod: billing.payment_method || 'No payment method on file'
            },
            revenueHistory: billing.billing_history || [],
            savingsComparison: {
              // If agents bought individually, they would pay full price for Essential
              individualCost: agentCounts.total * 50, // All agents would pay $50 for Essential
              brokerageCost: billing.current_month.total_amount, // Brokerage pays $1000 + $50 per agent
              totalSavings: Math.max(0, (agentCounts.total * 50) - billing.current_month.total_amount),
              savingsPercentage: 0 // No direct savings since brokerage also pays $50 per agent
            }
          });
        } else {
          setError('Failed to load billing data');
        }
      } catch (err) {
        console.error('Error fetching billing data:', err);
        setError('Unable to connect to server - API endpoint may not be available');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, [brokerageId]);

  // Fetch billing history for invoices from Stripe
  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        if (!brokerageId) return;
        
        // Use axiosInstance for consistency
        const axiosInstance = (await import('../../helpers/api')).default;
        
        // Fetch invoices directly from Stripe via user endpoint
        const response = await axiosInstance.get('/users/invoices', {
          params: { limit: 12 }
        });
        
        const data = response.data;
        console.log('Invoice fetch response:', data);

        // Backend returns invoices at data.data.invoices
        const invoicesArray = data.data?.invoices || data.invoices || [];

        if (data.success) {
          console.log(`Found ${invoicesArray.length} invoices`);

          if (invoicesArray.length > 0) {
            const invoiceData = invoicesArray.map((invoice: any) => ({
              id: invoice.invoice_number || invoice.id,
              date: new Date(invoice.created * 1000).toISOString(),
              dueDate: new Date((invoice.created + 15 * 24 * 60 * 60) * 1000).toISOString(),
              amount: invoice.amount_paid,
              status: invoice.paid ? 'paid' : (invoice.status === 'open' ? 'pending' : 'overdue'),
              description: invoice.description,
              agentCount: billingData?.currentBilling.totalAgentCount || 0,
              pdfUrl: invoice.pdf_url,
              hostedUrl: invoice.hosted_invoice_url,
              breakdown: {
                // Use tier-based pricing from billingData (populated from backend)
                baseFee: billingData?.currentBilling.baseFee || 1500, // Default to small tier
                agentFees: billingData?.currentBilling.agentFees || 0,
                professionalUpgrades: 0,
                premiumUpgrades: 0
              },
              // Include line items from Stripe
              lines: invoice.lines
            }));
            setInvoices(invoiceData);
          } else {
            // API succeeded but returned 0 invoices
            setInvoices([]);
          }
        }
      } catch (err: any) {
        console.error('Error fetching Stripe invoices:', err);
        console.error('Error details:', err.response?.data || err.message);
        // Show a toast or alert about invoice loading issue
        if (err.response?.status === 404) {
          console.log('Invoice endpoint not found - check backend routes');
        }
        // Don't set error here as billing data is more important than history
        // Set empty invoices so UI shows "No invoices"
        setInvoices([]);
      }
    };

    if (billingData) {
      fetchBillingHistory();
    }
  }, [brokerageId, billingData]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { bg: 'success', icon: faCheckCircle, text: 'Paid' },
      pending: { bg: 'warning', icon: faClock, text: 'Pending' },
      overdue: { bg: 'danger', icon: faExclamationTriangle, text: 'Overdue' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    return (
      <Badge bg={config.bg} className="d-flex align-items-center">
        <FontAwesomeIcon icon={config.icon} className="me-1" size="xs" />
        {config.text}
      </Badge>
    );
  };

  const CurrentBillingCard: React.FC = () => {
    // KOPS-329: Calculate tier-based billing
    const agentCount = billingData?.currentBilling.totalAgentCount || 0;
    const tierCalculation = calculateMonthlyTotal(agentCount, hasAiWebsite);
    const currentTier = getCurrentTier(agentCount);
    const tierInfo = BROKER_PRICING_TIERS.tiers[currentTier];
    const isApproachingLimit = isApproachingTierLimit(agentCount, currentTier);
    const nextTier = getNextTier(currentTier);

    return (
      <Card className="h-100 border-0 ai-enhanced-card">
        <Card.Header
          className="border-bottom-0 py-4"
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px 16px 0 0'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faHandHoldingDollar}
                className="me-2"
                style={{ color: '#10b981', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                Current Billing Summary
              </h5>
            </div>
            <Badge
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                fontSize: '11px'
              }}
            >
              {tierCalculation.tierName}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="text-center mb-4">
            <div className="display-4 fw-bold text-success mb-2">
              ${tierCalculation.total.toLocaleString()}
            </div>
            <p className="text-muted mb-1">Current Monthly Total</p>
            <small className="text-muted">
              Next billing: {billingData?.currentBilling.nextBillingDate ? new Date(billingData.currentBilling.nextBillingDate).toLocaleDateString() : 'N/A'}
            </small>
          </div>

          {/* Tier upgrade warning */}
          {isApproachingLimit && nextTier && (
            <Alert variant="warning" className="mb-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong>Approaching Tier Limit!</strong>
              <br />
              <small>
                You have {agentCount} agents (90% of {tierInfo.maxAgents} max).
                You'll be upgraded to {nextTier.name} (${nextTier.monthlyPrice}/month platform fee) when you exceed {tierInfo.maxAgents} agents.
              </small>
            </Alert>
          )}

          <div className="space-y-3">
            <div className="d-flex justify-content-between align-items-center p-3 rounded-3"
                 style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faProjectDiagram} className="me-2 text-muted" />
                <div>
                  <div>Platform Fee - {tierCalculation.tierName}</div>
                  <small className="text-muted">{tierInfo.minAgents}-{tierInfo.maxAgents === 999999 ? '∞' : tierInfo.maxAgents} agents</small>
                </div>
              </div>
              <span className="fw-bold">${tierCalculation.tierPrice.toLocaleString()}</span>
            </div>

            <div className="d-flex justify-content-between align-items-center p-3 rounded-3"
                 style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                <span>Agent Access Fees ({agentCount} agents × $70)</span>
              </div>
              <span className="fw-bold text-primary">${tierCalculation.agentFees.toLocaleString()}</span>
            </div>

            {hasAiWebsite && (
              <div className="d-flex justify-content-between align-items-center p-3 rounded-3"
                   style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.3)' }}>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
                  <span>AI Home Search Website</span>
                </div>
                <span className="fw-bold" style={{ color: '#6366f1' }}>$500</span>
              </div>
            )}

            <div className="mt-3 p-3 rounded-3" style={{ background: 'rgba(147, 197, 253, 0.1)', border: '1px solid rgba(147, 197, 253, 0.3)' }}>
              <div className="text-center mb-2">
                <strong className="text-primary">AI Upgrades (Paid by Agents)</strong>
              </div>
              <div className="small">
                {(billingData?.currentBilling.agentBreakdown.professional.count ?? 0) > 0 && (
                  <div className="d-flex justify-content-between mb-1">
                    <span>• Professional AI: {billingData?.currentBilling.agentBreakdown.professional.count} agents</span>
                    <span className="text-muted">@ $200 ea</span>
                  </div>
                )}
                {(billingData?.currentBilling.agentBreakdown.dealai.count ?? 0) > 0 && (
                  <div className="d-flex justify-content-between mb-1">
                    <span>• Deal AI: {billingData?.currentBilling.agentBreakdown.dealai.count} agents</span>
                    <span className="text-muted">@ $300 ea</span>
                  </div>
                )}
                {(billingData?.currentBilling.agentBreakdown.premium.count ?? 0) > 0 && (
                  <div className="d-flex justify-content-between mb-1">
                    <span>• Agent Kairos: {billingData?.currentBilling.agentBreakdown.premium.count} agents</span>
                    <span className="text-muted">@ $450 ea</span>
                  </div>
                )}
                {((billingData?.currentBilling.agentBreakdown.professional.count ?? 0) === 0 &&
                  (billingData?.currentBilling.agentBreakdown.dealai.count ?? 0) === 0 &&
                  (billingData?.currentBilling.agentBreakdown.premium.count ?? 0) === 0) && (
                  <div className="text-center text-muted">No AI upgrades active</div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Subtotal (Platform + Agents):</span>
              <span className="fw-bold">${tierCalculation.subtotal.toLocaleString()}</span>
            </div>
            {hasAiWebsite && (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">AI Website Add-on:</span>
                <span className="fw-bold">${tierCalculation.websiteFee.toLocaleString()}</span>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
              <span className="fw-bold">Monthly Total:</span>
              <span className="fw-bold text-success fs-5">${tierCalculation.total.toLocaleString()}</span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-bold">Payment Method:</span>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCreditCard} className="me-2 text-muted" />
                <small className="text-muted">{billingData?.currentBilling.paymentMethod}</small>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const BrokerageValueCard: React.FC = () => (
    <Card className="h-100 border-0 ai-enhanced-card">
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(14, 165, 233, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={faArrowTrendUp} 
            className="me-2"
            style={{ color: '#00d4ff', fontSize: '20px' }}
          />
          <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
            Brokerage Value Analysis
          </h5>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="text-center mb-4">
          <div className="display-6 fw-bold text-primary mb-2">
            ${billingData?.currentBilling.baseFee.toLocaleString()}
          </div>
          <p className="text-muted mb-1">Platform Investment</p>
          <Badge bg="primary" style={{ fontSize: '12px' }}>
            Unlimited Agent Seats
          </Badge>
        </div>

        <div className="mb-4">
          <h6 className="fw-bold mb-3">Included Platform Features</h6>
          <div className="space-y-2">
            <div className="d-flex align-items-start p-2 rounded" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 mt-1" />
              <div>
                <strong className="text-dark">Team Management Dashboard</strong>
                <div className="small text-muted">Agent directory, performance tracking, and analytics</div>
              </div>
            </div>
            
            <div className="d-flex align-items-start p-2 rounded" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 mt-1" />
              <div>
                <strong className="text-dark">Document Management System</strong>
                <div className="small text-muted">Centralized storage, sharing, and compliance tracking</div>
              </div>
            </div>
            
            <div className="d-flex align-items-start p-2 rounded" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 mt-1" />
              <div>
                <strong className="text-dark">Inter-office Communication</strong>
                <div className="small text-muted">Team messaging, announcements, and collaboration tools</div>
              </div>
            </div>
            
            <div className="d-flex align-items-start p-2 rounded" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 mt-1" />
              <div>
                <strong className="text-dark">Broker Assistant AI</strong>
                <div className="small text-muted">AI-powered support for operations and compliance</div>
              </div>
            </div>
            
            <div className="d-flex align-items-start p-2 rounded" style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2 mt-1" />
              <div>
                <strong className="text-dark">Commission Tracking</strong>
                <div className="small text-muted">Automated splits, calculations, and reporting</div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 rounded-3" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          <div className="text-center">
            <FontAwesomeIcon icon={faBolt} className="text-success me-2" />
            <span className="fw-bold text-success">
              Agent AI Discounts: $50/month savings on upgrades
            </span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const AIAdoptionCard: React.FC = () => {
    // Calculate AI adoption percentages
    const totalAgents = billingData?.currentBilling.totalAgentCount || 0;
    const essentialCount = billingData?.currentBilling.agentBreakdown.essential.count || 0;
    const professionalCount = billingData?.currentBilling.agentBreakdown.professional.count || 0;
    const dealAICount = billingData?.currentBilling.agentBreakdown.dealai.count || 0;
    const agentKairosCount = billingData?.currentBilling.agentBreakdown.premium.count || 0;
    
    const aiAgentsTotal = professionalCount + dealAICount + agentKairosCount;
    const adoptionRate = totalAgents > 0 ? Math.round((aiAgentsTotal / totalAgents) * 100) : 0;
    
    return (
      <Card className="h-100 border-0 ai-enhanced-card">
        <Card.Header 
          className="border-bottom-0 py-4"
          style={{ 
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(79, 70, 229, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px 16px 0 0'
          }}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faChartLine} 
              className="me-2"
              style={{ color: '#6366f1', fontSize: '20px' }}
            />
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
              AI Feature Adoption
            </h5>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="text-center mb-4">
            <div className="position-relative d-inline-block">
              {/* Simple circular progress indicator */}
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: `conic-gradient(#6366f1 0deg ${adoptionRate * 3.6}deg, #e5e7eb ${adoptionRate * 3.6}deg 360deg)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <div className="display-6 fw-bold" style={{ color: '#6366f1' }}>{adoptionRate}%</div>
                  <small className="text-muted">AI Adoption</small>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold mb-3">Agent Distribution</h6>
            <div className="space-y-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <div className="rounded-circle me-2" style={{width: '10px', height: '10px', background: '#e5e7eb'}}></div>
                  <span>Essential Only</span>
                </div>
                <span className="fw-semibold">{essentialCount} agents</span>
              </div>
              
              {professionalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle me-2" style={{width: '10px', height: '10px', background: '#0ea5e9'}}></div>
                    <span>Professional AI</span>
                  </div>
                  <span className="fw-semibold">{professionalCount} agents</span>
                </div>
              )}
              
              {dealAICount > 0 && (
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle me-2" style={{width: '10px', height: '10px', background: '#6366f1'}}></div>
                    <span>Deal AI</span>
                  </div>
                  <span className="fw-semibold">{dealAICount} agents</span>
                </div>
              )}
              
              {agentKairosCount > 0 && (
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle me-2" style={{width: '10px', height: '10px', background: '#10b981'}}></div>
                    <span>Agent Kairos</span>
                  </div>
                  <span className="fw-semibold">{agentKairosCount} agents</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-3 rounded-3" style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)' }}>
            <div className="text-center">
              <FontAwesomeIcon icon={faArrowTrendUp} className="me-2" style={{ color: '#6366f1' }} />
              <div className="small">
                <strong style={{ color: '#6366f1' }}>AI-Powered Sales Growth</strong>
                <div className="text-muted mt-1">Agents with AI features close 35% more deals</div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripeConfig = await configService.getStripeConfig();
        const stripe = loadStripe(stripeConfig.publishableKey);
        setStripePromise(stripe);
        console.log('✅ [Stripe] Initialized with secure publishable key');
      } catch (error) {
        console.warn('⚠️ [Stripe] Failed to get secure config, using fallback');
        const fallbackKey = process.env.REACT_APP_STRIPE_PUB_KEY as string;
        if (fallbackKey) {
          const stripe = loadStripe(fallbackKey);
          setStripePromise(stripe);
        } else {
          console.error('❌ [Stripe] No publishable key available');
          toast.error('Payment system unavailable. Please contact support.');
        }
      }
    };

    initializeStripe();
  }, []);

  // Subscription handlers
  const handleBuyClick = (tier: 'stability' | 'small' | 'medium' | 'large', price: number) => {
    setSelectedTier(tier);
    setSelectedPrice(price);
    setShowSubscriptionModal(true);
  };

  // Handler for broker pricing subscription
  const handleBrokerPricingSubscribeClick = (subscriptionType: 'per-office' | 'transaction-coordinator') => {
    setSelectedSubscriptionType(subscriptionType);
    setShowBrokerPricingModal(true);
  };

  // Handler for confirming broker pricing subscription
  const handleBrokerPricingConfirm = async () => {
    try {
      setProcessingSubscription(true);

      // Create Stripe checkout session for $500/month brokerage plan
      const response = await axiosInstance.post('/users/create_checkout_session', {
        subscription_type: 'BrokerTeamPlan',  // Fixed: Changed from 'Essential' ($35) to 'BrokerTeamPlan' ($500/month)
        subscription_interval: 'monthly',
        product_id: 'prod_TZzXCL370MzV69',  // Stripe "Stability Plan" product
        price_id: 'price_1ScpYUGOTrR7B5AAF4jMgozx',  // $500/month price
        brokerage_id: brokerageId,
        agent_count: billingData?.currentBilling.totalAgentCount || 0
      });

      if (response.data.success && response.data.data) {
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe not initialized');
        }

        const result = await stripe.redirectToCheckout({
          sessionId: response.data.data
        });

        if (result.error) {
          throw result.error;
        }
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to process subscription');
    } finally {
      setProcessingSubscription(false);
    }
  };

  const handleSubscriptionConfirm = async (tier: 'standard' | 'stability' | 'small' | 'medium' | 'large') => {
    try {
      // Create Stripe checkout session
      const response = await axiosInstance.post('/broker/create-subscription-checkout', {
        brokerageId,
        tier,
        priceId: tier === 'standard' ? 'price_1ScpYUGOTrR7B5AAF4jMgozx' : tier === 'stability' ? 'price_1ScpYUGOTrR7B5AAF4jMgozx' : tier === 'small' ? 'price_small' : tier === 'medium' ? 'price_medium' : 'price_large'
      });

      if (response.data.success && response.data.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err: any) {
      console.error('Subscription error:', err);
      throw new Error(err.response?.data?.message || 'Failed to process subscription');
    }
  };

  const InvoiceModal: React.FC = () => (
    <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} size="lg">
      <Modal.Header 
        closeButton
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
          Invoice Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedInvoice && (
          <div>
            <Row className="mb-4">
              <Col md={6}>
                <h6 className="fw-bold mb-3">Invoice Information</h6>
                <div className="mb-2">
                  <strong>Invoice ID:</strong> {selectedInvoice.id}
                </div>
                <div className="mb-2">
                  <strong>Date:</strong> {new Date(selectedInvoice.date).toLocaleDateString()}
                </div>
                <div className="mb-2">
                  <strong>Due Date:</strong> {new Date(selectedInvoice.dueDate).toLocaleDateString()}
                </div>
                <div className="mb-2">
                  <strong>Status:</strong> {getStatusBadge(selectedInvoice.status)}
                </div>
              </Col>
              <Col md={6}>
                <h6 className="fw-bold mb-3">Billing Details</h6>
                <div className="mb-2">
                  <strong>Total Amount:</strong> 
                  <span className="text-success fw-bold ms-1">${selectedInvoice.amount}</span>
                </div>
                <div className="mb-2">
                  <strong>Agent Count:</strong> {selectedInvoice.agentCount}
                </div>
                <div className="mb-2">
                  <strong>Description:</strong> {selectedInvoice.description}
                </div>
              </Col>
            </Row>

            <h6 className="fw-bold mb-3">Cost Breakdown</h6>
            <Table responsive>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Rate</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Platform Access (Per Office)</td>
                  <td>1</td>
                  <td>${(selectedInvoice.breakdown.baseFee || 1500).toFixed(2)}</td>
                  <td>${(selectedInvoice.breakdown.baseFee || 1500).toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Agent Fees</td>
                  <td>{selectedInvoice.agentCount || 0}</td>
                  <td>${((selectedInvoice.breakdown.agentFees || 0) / (selectedInvoice.agentCount || 1)).toFixed(2)}</td>
                  <td>${(selectedInvoice.breakdown.agentFees || 0).toFixed(2)}</td>
                </tr>
                <tr className="table-success">
                  <td><strong>Total</strong></td>
                  <td></td>
                  <td></td>
                  <td><strong>${selectedInvoice.amount}</strong></td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowInvoiceModal(false)}>
          Close
        </Button>
        {selectedInvoice?.hostedUrl && (
          <Button 
            variant="outline-primary"
            onClick={() => window.open(selectedInvoice.hostedUrl, '_blank')}
          >
            <FontAwesomeIcon icon={faEye} className="me-1" />
            View Online
          </Button>
        )}
        {selectedInvoice?.pdfUrl && (
          <Button 
            variant="outline-primary"
            onClick={() => window.open(selectedInvoice.pdfUrl, '_blank')}
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Download PDF
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading billing information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        <FontAwesomeIcon icon={faBolt} className="me-2" />
        {error}
      </Alert>
    );
  }

  if (!billingData) {
    return (
      <Alert variant="warning" className="m-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        No billing data available
      </Alert>
    );
  }

  // Test invoice generation (only in development/test mode)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const generateTestInvoice = async () => {
    try {
      console.log('Creating test invoice with data:', {
        brokerageId: brokerageId,
        agentCount: billingData.currentBilling.totalAgentCount || 8,
        markAsPaid: true
      });
      
      // Use axiosInstance to handle API calls properly across environments
      const axiosInstance = (await import('../../helpers/api')).default;
      
      const response = await axiosInstance.post('/users/create-test-invoice', {
        brokerageId: brokerageId,
        agentCount: billingData.currentBilling.totalAgentCount || 8,
        markAsPaid: false  // Let Stripe handle payment status naturally
      });
      
      console.log('Response:', response);
      const data = response.data;
      
      if (data.success) {
        alert(`Test invoice created successfully!\n\nInvoice Number: ${data.data.invoice_number}\nAmount: $${data.data.amount_due}\n\nRefreshing invoice list...`);
        // Refresh the invoice list
        window.location.reload();
      } else {
        alert('Failed to create test invoice: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error creating test invoice:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error data:', error.response.data);
        console.error('Error status:', error.response.status);
        alert(`Failed to create test invoice: ${error.response.status} - ${error.response.data?.error || error.response.data?.message || 'Server error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        alert('Error: No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        alert(`Error creating test invoice: ${error?.message || 'Unknown error'}`);
      }
    }
  };

  return (
    <div className="broker-billing">
      
      {/* AI Intelligence Header */}
      <Card 
        className="border-0 mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-4"
                style={{ 
                  width: "56px", 
                  height: "56px",
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 0 25px rgba(16, 185, 129, 0.5)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faDollarSign} 
                  className="text-white" 
                  size="lg"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.5))' }}
                />
              </div>
              <div>
                <h3 className="mb-1 fw-bold" style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                }}>
                  Billing & Revenue Management
                </h3>
                <p className="mb-0 text-muted">
                  Smart billing insights and cost optimization
                </p>
              </div>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '12px 20px',
              borderRadius: '25px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              <div className="d-flex align-items-center">
                <div className="rounded-circle me-2" style={{
                  width: '10px', 
                  height: '10px', 
                  background: '#10b981',
                  animation: 'pulse 2s infinite'
                }} />
                <span className="fw-semibold text-success" style={{ 
                  fontSize: '14px', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px'
                }}>
                  <FontAwesomeIcon icon={faBolt} className="me-1" />
                  AUTO-BILLING ACTIVE
                </span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* KOPS-329: Implementation Status Card */}
      {implementationDate && (
        <ImplementationStatusCard
          implementationPaid={implementationPaid}
          implementationDate={implementationDate}
          onboardingStatus={onboardingStatus}
        />
      )}

      {/* Billing Overview Cards */}
      <Row className="g-4 mb-4">
        <Col lg={4}>
          <CurrentBillingCard />
        </Col>
        <Col lg={4}>
          <BrokerageValueCard />
        </Col>
        <Col lg={4}>
          <AIAdoptionCard />
        </Col>
      </Row>

      {/* KOPS-329: Simple Flat Pricing Display */}
      <SimpleBrokeragePricingCard
        agentCount={billingData?.currentBilling.totalAgentCount || 0}
        onSubscribeClick={handleBrokerPricingSubscribeClick}
      />

      {/* Invoice History */}
      <Row className="g-4 mt-2">
        <Col>
          <Card className="border-0 ai-enhanced-card">
            <Card.Header 
              className="border-bottom-0 py-4"
              style={{ 
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px 16px 0 0'
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={faFileInvoice} 
                    className="me-2"
                    style={{ color: '#6366f1', fontSize: '20px' }}
                  />
                  <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
                    Invoice History
                  </h5>
                  <Badge bg="info" className="ms-2">{invoices.length} invoices</Badge>
                </div>
                <div>
                  <Button 
                    size="sm" 
                    variant="outline-secondary" 
                    className="me-2"
                    onClick={() => window.location.reload()}
                  >
                    <FontAwesomeIcon icon={faArrowUp} className="me-1" />
                    Refresh
                  </Button>
                  <Button size="sm" variant="outline-primary">
                    <FontAwesomeIcon icon={faDownload} className="me-1" />
                    Export All
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead style={{ background: 'rgba(99, 102, 241, 0.05)' }}>
                    <tr>
                      <th className="border-0 py-3 px-4">Invoice</th>
                      <th className="border-0 py-3">Amount</th>
                      <th className="border-0 py-3">Status</th>
                      <th className="border-0 py-3">Date</th>
                      <th className="border-0 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length > 0 ? invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="py-3 px-4">
                          <div>
                            <div className="fw-semibold">{invoice.id}</div>
                            <small className="text-muted">{invoice.description}</small>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="fw-bold">${invoice.amount.toLocaleString()}</div>
                          <small className="text-muted">{invoice.agentCount} agents</small>
                        </td>
                        <td className="py-3">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="py-3">
                          <div>{new Date(invoice.date).toLocaleDateString()}</div>
                          <small className="text-muted">
                            Due: {new Date(invoice.dueDate).toLocaleDateString()}
                          </small>
                        </td>
                        <td className="py-3">
                          <Button 
                            size="sm" 
                            variant="outline-primary" 
                            className="me-2"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowInvoiceModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button size="sm" variant="outline-success">
                            <FontAwesomeIcon icon={faDownload} />
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="text-center py-5 text-muted">
                          No invoice history available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <InvoiceModal />

      {/* Subscription Modal */}
      <BrokerageSubscriptionModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        tier={selectedTier}
        price={selectedPrice}
        onConfirm={handleSubscriptionConfirm}
      />

      {/* Broker Pricing Subscription Modal */}
      <BrokerPricingSubscriptionModal
        show={showBrokerPricingModal}
        onHide={() => setShowBrokerPricingModal(false)}
        onConfirm={handleBrokerPricingConfirm}
        loading={processingSubscription}
        subscriptionType={selectedSubscriptionType}
        agentCount={billingData?.currentBilling.totalAgentCount || 0}
      />
    </div>
  );
};

export default BrokerBilling;